import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

export default function TermsPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'パタカラ®公式ライセンス利用規約',
      description: 'パタカラ®公式施設ライセンスの利用規約。登録商標「パタカラ®」の使用許諾条件、禁止事項、契約終了後の義務などを定めます。',
      url: `${siteUrl}/terms`,
      inLanguage: 'ja',
      isPartOf: {
        '@type': 'WebSite',
        name: 'パタカラ®公式施設ライセンス',
        url: `${siteUrl}/`,
      },
    };

    const existing = document.querySelectorAll('script[data-schema="terms"]');
    existing.forEach((s) => s.remove());

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-schema', 'terms');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.querySelectorAll('script[data-schema="terms"]').forEach((s) => s.remove());
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAnchorNav = (anchor: string) => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById(anchor);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }, 400);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://static.readdy.ai/image/1827e98d95d9f7558266c3fb82d75725/927c3b547f45ed1f38c15098c60865bf.png" 
              alt="パタカラ®ロゴ" 
              className="h-12 w-auto object-contain"
            />
          </Link>
          <Link 
            to="/"
            className="px-6 py-2.5 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-all whitespace-nowrap cursor-pointer"
          >
            ホームへ戻る
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-orange-50 to-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-serif font-bold text-slate-800 mb-4">
            パタカラ®公式ライセンス利用規約
          </h1>
          <p className="text-base text-gray-600">
            最終更新日：2026年4月1日
          </p>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10">
            
            {/* 第1条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">1</span>
                第1条（目的）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  本規約は、株式会社パックスシステムズ（以下「当社」）が保有する登録商標「パタカラ®」（登録第5657576号、以下「本商標」）の使用許諾条件を定めるものです。
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  本契約は商標使用許諾契約であり、フランチャイズ契約、代理店契約、業務提携契約、医療契約ではありません。
                </p>
              </div>
            </article>

            {/* 第2条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">2</span>
                第2条（契約の成立）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  利用者が申込を行い、当社が承認した時点で契約成立とします。
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  利用料の決済は当社指定の決済代行業者（会費ペイ）を通じて行われます。
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  決済は収納代行であり、契約主体は当社と利用者です。
                </p>
              </div>
            </article>

            {/* 第3条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">3</span>
                第3条（許諾の範囲）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  当社は契約期間中、以下に限定して本商標の使用を許諾します。
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>パタカラ®の登録商標使用権<br />当社指定ロゴデータの使用</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>「パタカラ®公式施設」の表示</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>当社発行の認定証掲示</span>
                  </li>
                </ul>
                <p className="text-base text-gray-700 leading-relaxed">
                  許諾は非独占的・譲渡不可・再許諾不可・非恒久的とします。
                </p>
              </div>
            </article>

            {/* 第4条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">4</span>
                第4条（商標管理義務）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  利用者は以下を遵守するものとします：
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>®表示をつけること</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>ロゴの改変・変形・色変更をしないこと</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>ガイドライン外の媒体利用をしないこと</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>本商標を自己の商号・サービス名に組み込まないこと</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>ドメイン名・SNSアカウント名に使用しないこと</span>
                  </li>
                </ul>
              </div>
            </article>

            {/* 第5条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">5</span>
                第5条（厳格な禁止事項）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  以下の行為を重大違反とします：
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-red-500 mt-1.5">•</span>
                    <span>医療効果・治療効果・改善保証の表示</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-red-500 mt-1.5">•</span>
                    <span>「公認」「医療監修」等の誤認表示</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-red-500 mt-1.5">•</span>
                    <span>商標を用いたセミナー、研修会、イベント等の開催（ただし、施設利用者への日常的な指導を除く）</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-red-500 mt-1.5">•</span>
                    <span>第三者への再許諾・転貸</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-red-500 mt-1.5">•</span>
                    <span>フランチャイズ展開</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-red-500 mt-1.5">•</span>
                    <span>商標を含む商標出願</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-red-500 mt-1.5">•</span>
                    <span>パタカラ類似名称の登録</span>
                  </li>
                </ul>
                <p className="text-base text-red-600 font-semibold leading-relaxed">
                  上記重大違反があった場合、即時解除とします。
                </p>
              </div>
            </article>

            {/* 第6条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">6</span>
                第6条（ブランド保護条項）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  利用者は、本商標の信用・価値を毀損する行為を行ってはなりません。
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  当社がブランド毀損のおそれがあると判断した場合、表示の修正または使用停止を命じることができます。
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  是正されない場合、契約を解除できます。
                </p>
              </div>
            </article>

            {/* 第7条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">7</span>
                第7条（監査権）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  当社は、本商標の使用状況を確認するため、資料提出または表示確認を求めることができます。
                </p>
              </div>
            </article>

            {/* 第8条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">8</span>
                第8条（未払い時の即時停止）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  利用料未払いが発生した場合、当社は商標使用を即時停止できます。
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  停止期間中の使用は無断使用とみなします。
                </p>
              </div>
            </article>

            {/* 第9条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">9</span>
                第9条（免責・非保障）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  ライセンス範囲を超えた使用により第三者の権利を侵害した場合、当社は責任を追うものではない。
                </p>
              </div>
            </article>

            {/* 第10条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">10</span>
                第10条（損害賠償）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  違反により当社に損害が生じた場合、利用者は損害賠償責任を負います。
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  商標無断使用期間中は、通常利用料の3倍相当額を違約金として請求できます。
                </p>
              </div>
            </article>

            {/* 第11条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">11</span>
                第11条（契約終了後の義務）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  契約終了後、利用者は直ちに：
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>ロゴ削除</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>ウェブサイト表記削除</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>SNS削除</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>印刷物使用停止</span>
                  </li>
                </ul>
                <p className="text-base text-gray-700 leading-relaxed">
                  を行うものとします。
                </p>
              </div>
            </article>

            {/* 第12条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">12</span>
                第12条（保証の否認）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  本ライセンスは商標使用許諾であり、医療効果・利用成果・経営成果を保証しません。
                </p>
              </div>
            </article>

            {/* 第13条 */}
            <article className="mb-12">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">13</span>
                第13条（利用料の改定）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  当社は、以下の事由が生じた場合、利用料を改定することができます。
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>物価変動</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>税制変更</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>社会経済情勢の変動</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>商標価値の向上</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>サービス内容の変更または拡充</span>
                  </li>
                  <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                    <span className="text-orange-500 mt-1.5">•</span>
                    <span>運営コストの増加</span>
                  </li>
                </ul>
                <p className="text-base text-gray-700 leading-relaxed">
                  利用料を改定する場合、当社は改定日の30日前までにWEB掲載または電子通知により告知します。
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  改定後の利用料は、告知後最初に到来する契約更新日から適用されます。
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  利用者が改定に同意しない場合、改定適用日前日までに解約することができます。
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  解約が行われない場合、利用者は改定後の利用料に同意したものとみなします。
                </p>
              </div>
            </article>

            {/* 第14条 */}
            <article className="mb-0">
              <h2 className="text-2xl font-bold text-slate-800 mb-4 flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">14</span>
                第14条（準拠法・管轄）
              </h2>
              <div className="ml-13 space-y-3">
                <p className="text-base text-gray-700 leading-relaxed">
                  日本法準拠
                </p>
                <p className="text-base text-gray-700 leading-relaxed">
                  横浜地方裁判所を専属的合意管轄とします
                </p>
              </div>
            </article>

          </div>

          {/* Back to Top Button */}
          <div className="mt-12 text-center">
            <button
              onClick={scrollToTop}
              className="inline-flex items-center gap-2 px-8 py-3 bg-orange-500 text-white text-sm font-semibold rounded-full hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-up-line text-lg"></i>
              ページトップへ
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="https://static.readdy.ai/image/1827e98d95d9f7558266c3fb82d75725/927c3b547f45ed1f38c15098c60865bf.png" 
                alt="パタカラ®ロゴ" 
                className="h-10 w-auto object-contain mb-4 bg-white rounded-lg p-1.5"
              />
              <p className="text-sm text-gray-400 leading-relaxed">
                介護施設・医療施設向けの公式ライセンスサービス
              </p>
            </div>
            
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">サービス</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">ホーム</Link></li>
                <li><Link to="/terms" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">利用規約</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">サポート</h4>
              <ul className="space-y-3">
                <li>
                  <button
                    onClick={() => handleAnchorNav('flow')}
                    className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0 text-left"
                  >
                    申込の流れ
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => handleAnchorNav('faq')}
                    className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer bg-transparent border-none p-0 text-left"
                  >
                    よくある質問
                  </button>
                </li>
                <li><Link to="/contact" className="text-sm text-gray-300 hover:text-white transition-colors cursor-pointer">お問い合わせ</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase mb-4 tracking-wider">会社情報</h4>
              <p className="text-sm text-gray-400 leading-relaxed mb-2">
                株式会社パックスシステムズ
              </p>
              <p className="text-xs text-gray-500 leading-relaxed mb-3">
                担当：関<br />
                〒231-0023<br />
                横浜市中区山下町1番地<br />
                シルクセンターM1F<br />
                TEL: 045-681-3165<br />
                FAX: 045-681-3173<br />
                メール：info@patakara.org
              </p>
              <p className="text-xs text-gray-500 leading-relaxed">
                登録商標「パタカラ®」<br />
                登録第5657576号
              </p>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-500 space-y-1">
                <p>© 2026 Pax Systems Co., Ltd. All rights reserved.</p>
                <p className="text-xs text-gray-500">「パタカラ®」は株式会社パックスシステムズの登録商標です（登録第5657576号）。</p>
                <p className="text-xs text-gray-500">本サイトは登録商標の指定役務の範囲内におけるライセンス提供について案内するものです。</p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}